import { useMutation } from '@tanstack/react-query';
import { Alert } from 'rsuite';
import { useElasticidadeCruzadaStore } from '../ElasticidadeCruzada.store';
import { getCurves, mapApiToElasticityResponse, mapApiToMarketShareResponse } from '../services';
import type { ElasticityCurveApiResponse, MarketShareApiParams, ProductFilterOption, ProdutoReferencia } from '../types';

const PRODUCT_IMAGE_BASE_URL = 'https://cdn.cosmos.bluesoft.com.br/products';

const buildProdutoReferenciaInfo = (product: ProductFilterOption): ProdutoReferencia => {
  const gtin = String(product.value);
  const label = product.label;
  const separatorIndex = label.indexOf(' - ');
  const nome = separatorIndex >= 0 ? label.substring(separatorIndex + 3) : label;

  return {
    gtin,
    nome,
    marca: '',
    imagemUrl: product.imagem || `${PRODUCT_IMAGE_BASE_URL}/${gtin}`,
  };
};

export const useElasticityData = () => {
  const {
    produtoReferencia,
    produtosCompetidores,
    tipoLocalizacao,
    localizacao,
    segmentos,
    subsegmentos,
    tipoConcorrente,
    calculoConcorrente,
    tipoShare,
    setState,
    setDataResponse,
    validateFilters,
  } = useElasticidadeCruzadaStore();

  const unifiedMutation = useMutation({
    mutationFn: (params: MarketShareApiParams) => getCurves(params),
    onSuccess: response => {
      if (response.status === 204 || response.status === 404) {
        setState('isEmptyResponse', true);
      } else {
        const curveType = tipoShare === 'VALOR' ? 'salesRevenue' : 'salesVolume';
        const productBarcode = (produtoReferencia.value?.[0] as string) || '';
        const cachedProduct = produtoReferencia.cache?.find(item => String(item.value) === productBarcode);
        const produtoReferenciaInfo = cachedProduct ? buildProdutoReferenciaInfo(cachedProduct as ProductFilterOption) : null;
        const apiResponse = response.data as ElasticityCurveApiResponse;
        const elasticity = mapApiToElasticityResponse(apiResponse, curveType);
        const competitorMode = tipoConcorrente === 'TODOS' && calculoConcorrente === 'MEDIA' ? 'AVERAGE' : 'INDIVIDUAL';
        const marketShare = mapApiToMarketShareResponse(apiResponse, produtoReferenciaInfo, competitorMode);

        setState('isEmptyResponse', false);
        setDataResponse({
          elasticity: elasticity.data,
          marketShare: marketShare.data,
          rawMarketShareApiResponse: apiResponse,
          produtoReferenciaInfo: produtoReferenciaInfo,
        });
      }
    },
    onError: (error: any) => {
      setDataResponse({ elasticity: null, marketShare: null, rawMarketShareApiResponse: null, produtoReferenciaInfo: null });

      const status = error?.response?.status;

      if (status === 204 || status === 404) {
        setState('isEmptyResponse', true);
        return;
      }

      Alert.error('Erro ao buscar dados de elasticidade. Tente novamente.', 5000);
    },
  });

  const fetchData = async () => {
    if (!validateFilters()) {
      Alert.warning('Para visualizar os resultados, defina todos os campos de busca', 5000);
      return;
    }

    const productBarcode = (produtoReferencia.value?.[0] as string) || '';
    const competitorBarcode = (produtosCompetidores.value?.[0] as string) || undefined;

    const params: MarketShareApiParams = {
      productBarcode,
      competitorBarcode,
      segmentation: (segmentos.value?.[0] as string) || undefined,
      subSegmentation: (subsegmentos.value?.[0] as string) || undefined,
      region: tipoLocalizacao === 'REGIAO' ? (localizacao.value?.[0] as string) || undefined : undefined,
    };

    setState('curvesPayload', params);
    unifiedMutation.mutate(params);
  };

  return {
    fetchData,
    isLoading: unifiedMutation.isPending,
  };
};
